import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useMemo, useState} from 'react';
import {noop} from 'underscore';
import _ from 'underscore';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

export type AccordionHeaderStyle = 'borderless' | 'bordered';

export const AccordionColors = [
  Color.GRAY_50,
  Color.GRAY_200,
  Color.RED_200,
  Color.ORANGE_300,
  Color.YELLOW_300,
  Color.GREEN_200,
  Color.TEAL_200,
  Color.BLUE_150,
  Color.PURPLE_200,
  '#FDE6E6',
  '#FCEDCE',
  '#FCF7CE',
  '#E0FDEE',
  '#E0F4FD',
  '#E8EDFF',
  '#F5ECFD',
];
export type AccordionColor = (typeof AccordionColors)[number];

export interface SharedAccordionProps {
  header: JSX.Element;
  headerStyle?: AccordionHeaderStyle;
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
  onToggle?: (isOpen: boolean) => void;
  /**
   * Which side of the header to render the expand icon
   *
   * @default 'left'
   */
  expandIconPosition?: 'left' | 'right';
  /**
   * Dictates whether default spacing is automatically added to the header
   *
   * @default 'default'
   */
  headerSpacing?: 'none' | 'default';
  headerColor?: AccordionColor;
}

/**
 * These props can be attached to an HTMLElement that is embedded in an Accordion header to prevent clicks
 * and keypresses from bubbling and toggling the accordion.
 */
export function preventAccordionToggleProps<T extends HTMLElement>(): {
  onClick: React.MouseEventHandler<T>;
  onKeyDown: React.KeyboardEventHandler<T>;
} {
  return {
    onClick: (e) => {
      e.stopPropagation();
    },
    onKeyDown: (e) => {
      e.stopPropagation();
    },
  };
}

export interface ControlledAccordionProps extends SharedAccordionProps {
  isOpen: boolean;
  setIsOpen: (newIsOpen: boolean) => void;
}

export interface AccordionProps extends SharedAccordionProps {
  isOpenByDefault?: boolean;
}

export function ControlledAccordion({
  header,
  headerStyle = 'borderless',
  children,
  isOpen,
  setIsOpen,
  dataTestAttrs = {component: 'Accordion', element: 'root'},
  onToggle = noop,
  expandIconPosition = 'left',
  headerSpacing = 'default',
  headerColor = Color.GRAY_50,
}: ControlledAccordionProps): JSX.Element {
  const accordionIcon = isOpen ? 'triangle-down' : 'triangle-right';

  const buttonId = useMemo(() => _.uniqueId('accordion-button-'), []);

  const doToggle = (): void => {
    setIsOpen(!isOpen);
    onToggle(!isOpen);
  };

  const expandIcon = (
    <BenchlingIcon
      icon={accordionIcon}
      className={classNames(
        css(styles.accordionIcon, headerStyle === 'bordered' && styles.accordionHeaderGrayIcon),
        'notebook-export--accordion-icon',
        headerStyle === 'bordered' && 'notebook-export--accordion-gray-icon'
      )}
    />
  );
  return (
    // TODO(BNCH-75467): Remove the duplicated styles which are used in temporary-aphrodite-styles.less
    <div
      className={classNames(
        css(styles.accordionWrapper, headerStyle === 'bordered' && styles.accordionWrapperGray),
        'notebook-export--accordion-wrapper',
        headerStyle === 'bordered' && `notebook-export--accordion-wrapper-gray`
      )}
      {...getTestProps(dataTestAttrs)}
    >
      <div
        aria-label={isOpen ? 'Collapse' : 'Expand'}
        className={classNames(css(styles.accordionButton), 'notebook-export--accordion-button')}
        id={buttonId}
        // Using a <button> via `<Button buttonStyle={ButtonStyle.UNBUTTON} />` will cause the button to
        // trigger its onClick handler when <SPACE> and <ENTER> is pressed, which is desirable. However, this
        // is a problem for Accordion usages where the header contains nested elements like
        // <input type="text"> where a space is a valid character to input. It doesn't seem possible to
        // prevent the bubbling of this event via `e.stopPropagation()` in the input's keydown handler,
        // seemingly related to the native translation of events that happens for <button> elements.
        // Fortunately, we can recreate the semantics of a button with `<div role="button">` and attach the
        // appropriate handler as covered in https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/button_role,
        // which *can* have their propagation stopped as shown in `preventAccordionToggleProps`.
        role="button"
        tabIndex={0}
        onClick={doToggle}
        onKeyDown={(e): void => {
          // Keypresses other then Enter and Space should not trigger a command
          if (e.key !== 'Enter' && e.key !== ' ') {
            return;
          }
          setIsOpen(!isOpen);
          onToggle(!isOpen);
        }}
        {...getTestProps({component: 'Accordion', element: 'toggleButton'})}
      >
        <div
          className={classNames(
            css(
              styles.accordionHeader,
              headerStyle === 'bordered' && styles.accordionHeaderSection,
              headerSpacing === 'default' && styles.accordionHeaderSpacing
            ),
            'notebook-export--accordion-header',
            headerStyle === 'bordered' && 'notebook-export--accordion-section-header',
            headerSpacing === 'default' && 'notebook-export--accordion-header-spacing'
          )}
          style={headerStyle === 'bordered' ? {backgroundColor: headerColor} : {}}
        >
          {expandIconPosition === 'left' && expandIcon}
          {header}
          {expandIconPosition === 'right' && expandIcon}
        </div>
      </div>
      {isOpen && (
        <div
          aria-labelledby={buttonId}
          className={classNames(css(headerSpacing === 'default' && styles.accordionContents), {
            'notebook-export--accordion-contents': headerSpacing === 'default',
          })}
          {...getTestProps({component: 'Accordion', element: 'contents'})}
          role="region"
        >
          {children}
        </div>
      )}
    </div>
  );
}

export default function Accordion({
  header,
  headerStyle = 'borderless',
  children,
  isOpenByDefault = false,
  dataTestAttrs = {component: 'Accordion', element: 'root'},
  onToggle = noop,
  expandIconPosition = 'left',
  headerSpacing = 'default',
  headerColor = Color.GRAY_50,
}: AccordionProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(isOpenByDefault);
  return (
    <ControlledAccordion
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      header={header}
      headerStyle={headerStyle}
      dataTestAttrs={dataTestAttrs}
      onToggle={onToggle}
      expandIconPosition={expandIconPosition}
      headerSpacing={headerSpacing}
      headerColor={headerColor}
    >
      {children}
    </ControlledAccordion>
  );
}

/**
 * We would like to keep these styles in sync with those in the duplicative temporary-aphrodite-styles.less
 * file, as exports do not currently include aphrodite styles.
 * For anyone editing these styles, please either include that synchronization in your changes if
 * straightforward, or inform the Notebook team to ensure correctness through a Slack ping or GitHub PR review
 * request. The Notebook team is willing to assist with validation / QA of the changes to ensure correct
 * behavior.
 */
const styles = StyleSheet.create({
  accordionWrapper: {
    borderBottom: `1px solid ${Color.GRAY_200}`,
    ':last-child': {
      borderBottom: 'none',
    },
  },
  accordionWrapperGray: {
    borderBottom: 'none',
    cursor: 'default',
  },
  accordionButton: {
    width: '100%',
    boxShadow: 'none',
  },
  accordionIcon: {
    color: Color.GRAY_400,
    width: 12,
  },
  accordionHeaderGrayIcon: {
    color: Color.GRAY_700,
    marginLeft: 10,
  },
  accordionHeader: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  accordionHeaderSpacing: {
    marginTop: 16,
    marginBottom: 16,
  },
  accordionHeaderSection: {
    border: 'transparent',
    boxShadow: `0px -2px 0px ${Color.GRAY_400}`,
  },
  accordionContents: {
    paddingBottom: 16,
  },
});
